(function () {
  const CUSTOM_RACKET_TAG = "custom-racket";
  const MIXED_FLAG = "akoMixedWarningShown";
  const CUSTOMIZER_SILENT_FLAG = "akoCustomizerSilentBlock";
  const FALLBACK_COPY = {
    conflict:
      "If your cart contains a custom racket, you cannot add regular items.",
    blockedByRegular:
      "If your cart contains regular items, you cannot add a custom racket. Please remove the regular items from your cart first.",
    mixedDetected:
      "Your cart contains both a custom racket and regular items. Custom rackets must be purchased on their own — please remove one or the other before checking out.",
    clearsCart:
      "Adding a custom racket to a cart that contains regular items will clear those items from your cart. Continue?",
    confirm: "Confirm",
    cancel: "Cancel",
  };

  const productTagCache = new Map();
  let cartIsMixed = false;

  // --- fetch interception ---------------------------------------------------
  const originalFetch = window.fetch;
  window.__akoOriginalFetch = originalFetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = readUrl(args[0]);
    if (url && url.includes("/cart/add")) {
      handleCartAddResponse(response.clone()).catch(() => {});
    }
    return response;
  };

  async function handleCartAddResponse(response) {
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      const serverMessage = data && (data.description || data.message);
      const message = serverMessage || (await pickFallbackMessage());
      showCartErrorOverlay(message);
      return;
    }
    const added = await response.json().catch(() => null);
    await reconcileCart(added);
  }

  async function pickFallbackMessage() {
    try {
      const cart = await fetchCart();
      if (!cart || !cart.items || cart.items.length === 0) return copy("conflict");
      const tagMap = await getTagsForHandles(uniqueHandles(cart.items));
      const hasCustom = cart.items.some((i) => isCustomLine(tagMap, i));
      const hasRegular = cart.items.some((i) => !isCustomLine(tagMap, i));
      if (hasRegular && !hasCustom) return copy("blockedByRegular");
      return copy("conflict");
    } catch (e) {
      return copy("conflict");
    }
  }

  // --- XHR interception (PageFly / jQuery $.ajax / classic carts) ----------
  const OriginalXHR = window.XMLHttpRequest;
  window.__akoOriginalXHR = OriginalXHR;
  function PatchedXHR() {
    const xhr = new OriginalXHR();
    let trackedUrl = "";
    const origOpen = xhr.open;
    xhr.open = function (method, url) {
      trackedUrl = String(url || "");
      return origOpen.apply(this, arguments);
    };
    xhr.addEventListener("loadend", () => {
      if (!trackedUrl.includes("/cart/add")) return;
      try {
        const text = xhr.responseText || "";
        const data = text ? JSON.parse(text) : null;
        if (xhr.status >= 200 && xhr.status < 300) {
          reconcileCart(data).catch(() => {});
        } else {
          const serverMessage = data && (data.description || data.message);
          if (serverMessage) {
            showCartErrorOverlay(serverMessage);
          } else {
            pickFallbackMessage()
              .then((m) => showCartErrorOverlay(m))
              .catch(() => {});
          }
        }
      } catch (e) {}
    });
    return xhr;
  }
  PatchedXHR.prototype = OriginalXHR.prototype;
  window.XMLHttpRequest = PatchedXHR;

  // --- theme-dispatched error event ----------------------------------------
  document.addEventListener("cart:error", (event) => {
    const message = event.detail && event.detail.error;
    if (message) showCartErrorOverlay(message);
  });

  // --- core reconciliation --------------------------------------------------
  async function reconcileCart(addedPayload) {
    const cart = await fetchCart();
    if (!cart || !cart.items.length) {
      cartIsMixed = false;
      return;
    }

    const tagMap = await getTagsForHandles(uniqueHandles(cart.items));
    const cartHasCustom = cart.items.some((i) => isCustomLine(tagMap, i));
    const cartHasRegular = cart.items.some((i) => !isCustomLine(tagMap, i));

    if (!(cartHasCustom && cartHasRegular)) {
      cartIsMixed = false;
      return;
    }

    const addedItems = collectAddedItems(addedPayload);
    if (addedItems.length === 0) {
      cartIsMixed = true;
      return;
    }

    const addedAreCustom = addedItems.every((i) => isCustomLine(tagMap, i));
    const addedAreRegular = addedItems.every((i) => !isCustomLine(tagMap, i));

    if (addedAreRegular) {
      // Direction A: custom racket already in cart, user added regular → revert the add.
      const offendingKeys = addedItems.map((i) => i.key).filter(Boolean);
      await removeKeys(offendingKeys);
      showCartErrorOverlay(copy("conflict"));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      cartIsMixed = false;
      return;
    }

    if (addedAreCustom) {
      // Direction B: regulars already in cart, user added custom racket → ask before wiping.
      cartIsMixed = true;
      const customKeys = addedItems.map((i) => i.key).filter(Boolean);
      const regularKeys = cart.items
        .filter((i) => !isCustomLine(tagMap, i))
        .map((i) => i.key);

      showCartConfirmOverlay(copy("clearsCart"), {
        onConfirm: async () => {
          await removeKeys(regularKeys);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          cartIsMixed = false;
        },
        onCancel: async () => {
          await removeKeys(customKeys);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          cartIsMixed = false;
        },
      });
    }
  }

  // --- checkout guard -------------------------------------------------------
  document.addEventListener(
    "click",
    (e) => {
      if (!cartIsMixed) return;
      const trigger =
        e.target &&
        e.target.closest &&
        e.target.closest(
          'a[href*="/checkout"], button[name="checkout"], button[name="goto_pay"], input[name="checkout"]'
        );
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      showCartErrorOverlay(copy("conflict"));
    },
    true
  );

  document.addEventListener(
    "submit",
    (e) => {
      if (!cartIsMixed) return;
      const form = e.target;
      if (!form || !form.action) return;
      const action = String(form.action);
      const submitter = e.submitter;
      const isCheckoutAction = action.includes("/checkout");
      const isCartCheckoutSubmit =
        action.includes("/cart") &&
        submitter &&
        (submitter.name === "checkout" || submitter.name === "goto_pay");
      if (!isCheckoutAction && !isCartCheckoutSubmit) return;
      e.preventDefault();
      e.stopPropagation();
      showCartErrorOverlay(copy("conflict"));
    },
    true
  );

  // --- initial probes -------------------------------------------------------
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        refreshMixedStatus();
        checkCustomizerSilentBlock();
      },
      { once: true }
    );
  } else {
    refreshMixedStatus();
    checkCustomizerSilentBlock();
  }

  // Detect the cross-site customizer flow that JSONP-adds to /cart/add and
  // gets silently rejected by the cart-validation app. Trigger: customer is on
  // /cart, arrived from www.wilson.com, and the cart has regular items but no
  // custom racket (i.e. their customized racket was never added).
  async function checkCustomizerSilentBlock() {
    try {
      if (!/^\/cart\/?$/i.test(location.pathname)) return;
      const ref = document.referrer || "";
      if (!/^https?:\/\/(?:www\.)?wilson\.com\//i.test(ref)) return;

      if (safeSessionGet(CUSTOMIZER_SILENT_FLAG) === "1") return;

      const cart = await fetchCart();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

      // Definitive proof the customizer succeeded: any line with properties._custom_data.
      const customizerSucceeded = cart.items.some(hasCustomData);
      if (customizerSucceeded) return;

      const tagMap = await getTagsForHandles(uniqueHandles(cart.items));
      const hasCustom = cart.items.some((i) => isCustomLine(tagMap, i));
      const hasRegular = cart.items.some((i) => !isCustomLine(tagMap, i));
      if (hasCustom || !hasRegular) return;

      safeSessionSet(CUSTOMIZER_SILENT_FLAG, "1");
      showCartErrorOverlay(copy("blockedByRegular"));
    } catch (e) {}
  }

  async function refreshMixedStatus() {
    try {
      const cart = await fetchCart();
      if (!cart || !cart.items.length) {
        cartIsMixed = false;
        clearMixedFlag();
        return;
      }
      const tagMap = await getTagsForHandles(uniqueHandles(cart.items));
      const hasCustom = cart.items.some((i) => isCustomLine(tagMap, i));
      const hasRegular = cart.items.some((i) => !isCustomLine(tagMap, i));
      cartIsMixed = hasCustom && hasRegular;
      if (cartIsMixed) maybeWarnMixed();
      else clearMixedFlag();
    } catch (e) {
      cartIsMixed = false;
    }
  }

  function maybeWarnMixed() {
    if (safeSessionGet(MIXED_FLAG) === "1") return;
    safeSessionSet(MIXED_FLAG, "1");
    showCartErrorOverlay(copy("mixedDetected"));
  }

  function clearMixedFlag() {
    safeSessionRemove(MIXED_FLAG);
  }

  // --- helpers --------------------------------------------------------------
  function copy(key) {
    return (window.akoCartCopy && window.akoCartCopy[key]) || FALLBACK_COPY[key];
  }

  function readUrl(input) {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.href;
    if (input && input.url) return input.url;
    return "";
  }

  function uniqueHandles(items) {
    return [...new Set(items.map((i) => i.handle).filter(Boolean))];
  }

  function isCustom(tagMap, handle) {
    const tags = tagMap.get(handle);
    return Array.isArray(tags) && tags.includes(CUSTOM_RACKET_TAG);
  }

  function hasCustomData(item) {
    return !!(item && item.properties && item.properties._custom_data);
  }

  function isCustomLine(tagMap, item) {
    return isCustom(tagMap, item.handle) || hasCustomData(item);
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
  }

  async function removeKeys(keys) {
    const list = keys.filter(Boolean);
    if (list.length === 0) return;
    const updates = {};
    list.forEach((k) => (updates[k] = 0));
    await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates }),
    }).catch(() => {});
  }

  function collectAddedItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.id || payload.key || payload.handle) return [payload];
    return [];
  }

  async function getTagsForHandles(handles) {
    const out = new Map();
    await Promise.all(
      handles.map(async (handle) => {
        if (productTagCache.has(handle)) {
          out.set(handle, productTagCache.get(handle));
          return;
        }
        try {
          const product = await fetch(`/products/${handle}.js`, {
            credentials: "same-origin",
          }).then((r) => r.json());
          const tags = normalizeTags(product && product.tags);
          productTagCache.set(handle, tags);
          out.set(handle, tags);
        } catch (e) {
          productTagCache.set(handle, []);
          out.set(handle, []);
        }
      })
    );
    return out;
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map((t) => String(t).toLowerCase());
    if (typeof tags === "string")
      return tags
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    return [];
  }

  function safeSessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function safeSessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }
  function safeSessionRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {}
  }

  // --- overlays -------------------------------------------------------------
  function showCartErrorOverlay(message) {
    renderOverlay({ message, dismissible: true });
  }

  function showCartConfirmOverlay(message, { onConfirm, onCancel }) {
    renderOverlay({
      message,
      dismissible: false,
      actions: [
        {
          label: copy("cancel"),
          className: "ako-cart-error-btn--secondary",
          handler: onCancel,
        },
        {
          label: copy("confirm"),
          className: "ako-cart-error-btn--primary",
          handler: onConfirm,
        },
      ],
    });
  }

  function renderOverlay(opts) {
    const run = () => {
      if (!document.body) {
        document.addEventListener("DOMContentLoaded", run, { once: true });
        return;
      }
      document
        .querySelectorAll(".ako-cart-error-overlay")
        .forEach((el) => el.remove());

      const overlay = document.createElement("div");
      overlay.className = "ako-cart-error-overlay";

      const modal = document.createElement("div");
      modal.className = "ako-cart-error-modal";
      overlay.appendChild(modal);

      if (opts.dismissible) {
        const close = document.createElement("button");
        close.type = "button";
        close.className = "ako-cart-error-close";
        close.setAttribute("aria-label", "Close");
        close.textContent = "✕";
        modal.appendChild(close);
        close.addEventListener("click", () => overlay.remove());
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay) overlay.remove();
        });
        document.addEventListener(
          "keydown",
          (e) => {
            if (e.key === "Escape") overlay.remove();
          },
          { once: true }
        );
      }

      const icon = document.createElement("div");
      icon.className = "ako-cart-error-icon";
      icon.textContent = "⚠️";
      modal.appendChild(icon);

      const text = document.createElement("div");
      text.className = "ako-cart-error-message";
      text.textContent = opts.message;
      modal.appendChild(text);

      if (Array.isArray(opts.actions) && opts.actions.length) {
        const actions = document.createElement("div");
        actions.className = "ako-cart-error-actions";
        opts.actions.forEach((action) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "ako-cart-error-btn " + (action.className || "");
          btn.textContent = action.label;
          btn.addEventListener("click", () => {
            overlay.remove();
            if (typeof action.handler === "function") {
              Promise.resolve().then(action.handler).catch(() => {});
            }
          });
          actions.appendChild(btn);
        });
        modal.appendChild(actions);
      }

      document.body.appendChild(overlay);
    };
    run();
  }
})();
